import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card, Input, FloatGroup, ExternalLink } from 'modules/common';
import { emailValidator, isValidPhoneNumber, isValidURL, yesHTTPS } from 'core/utilities';

function UpsellContactCard({ editedUpsell, onUpdateEditedUpsell }) {
  // State to track when to show validation errors
  const [showValidationErrors, setShowValidationErrors] = useState({
    contact_card_first_name: false,
    contact_card_last_name: false,
    contact_card_organization_name: false,
  });

  // Check if name validation should pass
  const isNameValidationPassing = () => {
    const orgName = editedUpsell?.contact_card_organization_name;
    const firstName = editedUpsell?.contact_card_first_name;
    const lastName = editedUpsell?.contact_card_last_name;

    // Valid if org name exists OR both first and last name exist
    return (orgName && orgName.trim()) || (firstName && firstName.trim() && lastName && lastName.trim());
  };

  // Update validation errors when editedUpsell changes
  useEffect(() => {
    const isValid = isNameValidationPassing();
    if (isValid) {
      // Clear all validation errors if validation passes
      setShowValidationErrors({
        contact_card_first_name: false,
        contact_card_last_name: false,
        contact_card_organization_name: false,
      });
    }
  }, [
    editedUpsell?.contact_card_first_name,
    editedUpsell?.contact_card_last_name,
    editedUpsell?.contact_card_organization_name,
  ]);

  // Custom phone change handler
  const handlePhoneChange = event => {
    const { value } = event.target;
    const digitsOnly = value.replace(/\D/g, ''); // Store only digits

    // Limit to maximum 10 digits
    const limitedDigits = digitsOnly.slice(0, 10);

    // Create event with digits only for storage
    const formattedEvent = {
      ...event,
      target: {
        ...event.target,
        value: limitedDigits, // Store limited digits only
      },
    };

    onUpdateEditedUpsell(formattedEvent);
  };
  const formatForDisplay = value => {
    if (!value) return '';
    return formatPhoneNumber(value);
  };

  // Reusable phone validator function
  const phoneValidator = value => {
    if (!value) return true; // Allow empty

    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length < 5) return false; // Must be at least 5 digits
    if (digitsOnly.length <= 6) return true; // 5-6 digits are valid
    if (digitsOnly.length <= 10) return isValidPhoneNumber(value); // 7-10 digits use phone validator

    return false; // More than 10 digits is invalid (though this shouldn't happen due to input limiting)
  };

  // Updated validator that works with our state
  const nameValidator = fieldName => value => {
    // Get current values
    const orgName =
      fieldName === 'contact_card_organization_name' ? value : editedUpsell?.contact_card_organization_name;
    const firstName = fieldName === 'contact_card_first_name' ? value : editedUpsell?.contact_card_first_name;
    const lastName = fieldName === 'contact_card_last_name' ? value : editedUpsell?.contact_card_last_name;

    // If organization name exists, all fields are valid
    if (orgName && orgName.trim()) return true;

    // If no organization name, require both first and last name
    return firstName && firstName.trim() && lastName && lastName.trim();
  };

  // Custom handler for name fields
  const handleNameChange = event => {
    const { name } = event.target;

    // Update the field value first
    onUpdateEditedUpsell(event);

    // Mark this field as having been interacted with
    setShowValidationErrors(prev => ({
      ...prev,
      [name]: true,
    }));

    // If this change makes validation pass, clear all errors
    setTimeout(() => {
      if (isNameValidationPassing()) {
        setShowValidationErrors({
          contact_card_first_name: false,
          contact_card_last_name: false,
          contact_card_organization_name: false,
        });
      }
    }, 0);
  };

  // Handle blur events to show validation errors
  const handleNameBlur = event => {
    const { name } = event.target;

    if (!isNameValidationPassing()) {
      setShowValidationErrors(prev => ({
        ...prev,
        [name]: true,
      }));
    }
  };

  // Update the formatPhoneNumber function to handle the 10-digit limit
  const formatPhoneNumber = value => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');

    if (digitsOnly.length <= 5) {
      // 5 digits or less: no formatting
      return digitsOnly;
    } else if (digitsOnly.length === 6) {
      // 6 digits: XXX-XXX
      return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3)}`;
    } else if (digitsOnly.length === 7) {
      // 7 digits: XXX-XXXX
      return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3)}`;
    } else if (digitsOnly.length >= 8) {
      // 8-10 digits: (XXX) XXX-XXXX
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
    }

    return digitsOnly;
  };

  return (
    <>
      <Card.Title
        subtitle={
          <>
            Enter the organization information for the Contact Card. A First and Last Name or an Organization Name is
            required. Adding a Contact Card Image and at least one contact method (Phone Number or Email) is
            recommended.{' '}
            <ExternalLink href="https://support.winred.com/en/articles/3299578-types-of-upsells">
              Learn More.
            </ExternalLink>
          </>
        }
      >
        Contact Card Details
      </Card.Title>

      <FloatGroup>
        <Input
          type="text"
          name="contact_card_first_name"
          onChange={handleNameChange}
          validateOnBlurOnly={false} // Change to false so we can control validation
          value={editedUpsell?.contact_card_first_name || ''}
          id="contact_card_first_name"
          disabled={editedUpsell?._isDisabled}
          ariaLabel="contact_card_first_name"
          placeholder="First Name"
          validators={[nameValidator('contact_card_first_name')]}
          errorMessages={['Either Organization Name OR both First and Last Name are required.']}
          forceShowError={
            showValidationErrors.contact_card_first_name &&
            !nameValidator('contact_card_first_name')(editedUpsell?.contact_card_first_name)
          }
        />
      </FloatGroup>

      <FloatGroup>
        <Input
          type="text"
          name="contact_card_last_name"
          onChange={handleNameChange}
          validateOnBlurOnly={false}
          value={editedUpsell?.contact_card_last_name || ''}
          id="contact_card_last_name"
          disabled={editedUpsell?._isDisabled}
          ariaLabel="contact_card_last_name"
          placeholder="Last Name"
          validators={[nameValidator('contact_card_last_name')]}
          errorMessages={['Either Organization Name OR both First and Last Name are required.']}
          forceShowError={
            showValidationErrors.contact_card_last_name &&
            !nameValidator('contact_card_last_name')(editedUpsell?.contact_card_last_name)
          }
        />
      </FloatGroup>

      <FloatGroup>
        <Input
          type="text"
          name="contact_card_organization_name"
          onChange={handleNameChange}
          validateOnBlurOnly={false}
          value={editedUpsell?.contact_card_organization_name || ''}
          id="contact_card_organization_name"
          disabled={editedUpsell?._isDisabled}
          ariaLabel="contact_card_organization_name"
          placeholder="Organization Name"
          validators={[nameValidator('contact_card_organization_name')]}
          errorMessages={['Either Organization Name OR both First and Last Name are required.']}
          forceShowError={
            showValidationErrors.contact_card_organization_name &&
            !nameValidator('contact_card_organization_name')(editedUpsell?.contact_card_organization_name)
          }
        />
      </FloatGroup>
      <FloatGroup>
        <Input
          type="text"
          name="primary_email"
          onChange={onUpdateEditedUpsell}
          value={editedUpsell.primary_email || ''}
          validateOnBlurOnly={true}
          id="primary_email"
          disabled={editedUpsell?._isDisabled}
          ariaLabel="primary_email"
          placeholder="Primary Email"
          validators={[
            value => {
              if (!value) return true; // Allow empty
              return emailValidator.test(value); // Use .test() method on the regex
            },
          ]}
          errorMessages={['Please enter a valid email.']}
        />
      </FloatGroup>
      <FloatGroup>
        <Input
          type="text"
          name="secondary_email"
          onChange={onUpdateEditedUpsell}
          validateOnBlurOnly={true}
          value={editedUpsell?.secondary_email || ''}
          id="secondary_email"
          disabled={editedUpsell?._isDisabled}
          ariaLabel="secondary_email"
          placeholder="Secondary Email"
          validators={[
            value => {
              if (!value) return true; // Allow empty
              return emailValidator.test(value); // Use .test() method on the regex
            },
          ]}
          errorMessages={['Please enter a valid email.']}
        />
      </FloatGroup>
      <FloatGroup>
        <Input
          type="text"
          name="primary_phone_number"
          value={formatForDisplay(editedUpsell?.primary_phone_number)} // Format for display
          validateOnBlurOnly={true}
          onChange={handlePhoneChange}
          id="primary_phone_number"
          disabled={editedUpsell?._isDisabled}
          ariaLabel="primary_phone_number"
          placeholder="Primary Phone Number"
          validators={[phoneValidator]}
          errorMessages={['Please enter a phone number that is either 5, 6, or 10 digits long.']}
        />
      </FloatGroup>
      <FloatGroup>
        <Input
          type="text"
          name="secondary_phone_number"
          validateOnBlurOnly={true}
          value={formatForDisplay(editedUpsell?.secondary_phone_number)} // Format for display
          onChange={handlePhoneChange}
          id="secondary_phone_number"
          disabled={editedUpsell?._isDisabled}
          ariaLabel="secondary_phone_number"
          placeholder="Secondary Phone Number"
          validators={[phoneValidator]}
          errorMessages={['Please enter a phone number that is either 5, 6, or 10 digits long.']}
        />
      </FloatGroup>
      <FloatGroup>
        <Input
          type="text"
          name="organization_website"
          validateOnBlurOnly={true}
          value={editedUpsell?.organization_website || ''}
          onChange={onUpdateEditedUpsell}
          id="organization_website"
          disabled={editedUpsell?._isDisabled}
          ariaLabel="organization_website"
          placeholder="Organization Website"
          validators={[
            value => {
              if (!value) return true; // Allow empty
              // Check both valid URL format AND HTTPS requirement
              return isValidURL(value) && yesHTTPS(value);
            },
          ]}
          errorMessages={['Bad URL. Please enter a valid URL.']}
          description='Organization Website URL must include "https://".'
        />
      </FloatGroup>
    </>
  );
}

UpsellContactCard.propTypes = {
  editedUpsell: PropTypes.object.isRequired,
  onUpdateEditedUpsell: PropTypes.func.isRequired,
};

export default UpsellContactCard;
